/**
 * [Gurumi 툴바]
 * 역할: yesmoney rich-text-editor 툴바 포팅
 * 작성/수정일: 2026-07-29
 * 수정일: 2026-08-12 — 글자색·형광펜 메뉴 · 프리셋 features
 */
import { iconSvg } from '../icons.js';
import { createBlockMenu } from './block-menu.js';
import { createColorMenu } from './color-menu.js';
import { GURUMI_FEATURES_WRITE } from '../features.js';

function divider() {
  const el = document.createElement('span');
  el.className = 'g5-gurumi-toolbar-divider';
  el.setAttribute('aria-hidden', 'true');
  return el;
}

function toolBtn({ label, icon, active = false, disabled = false, onClick, extraClass = '' }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `g5-gurumi-tool-btn ${active ? 'is-active' : ''} ${extraClass}`.trim();
  btn.setAttribute('aria-label', label);
  if (active) btn.setAttribute('aria-pressed', 'true');
  btn.disabled = disabled;
  if (icon) btn.appendChild(iconSvg(icon));
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (!btn.disabled) onClick();
  });
  return btn;
}

/**
 * [Gurumi 툴바]
 * - 역할: features 플래그에 맞춰 버튼 구성
 * - 작성/수정일: 2026-08-12
 */
export function createToolbar(editor, handlers, mode, callbacks, features = GURUMI_FEATURES_WRITE) {
  const f = features || GURUMI_FEATURES_WRITE;
  const bar = document.createElement('div');
  bar.className = 'g5-gurumi-toolbar';
  bar.setAttribute('role', 'toolbar');
  bar.setAttribute('aria-label', mode === 'comment' ? '댓글 편집 도구' : '본문 편집 도구');

  const state = {
    undo: null,
    redo: null,
    bold: null,
    italic: null,
    colorMenu: null,
    link: null,
    unlink: null,
    image: null,
    youtube: null,
    left: null,
    center: null,
    right: null,
    alt: null,
    blockMenu: null,
  };

  if (f.blockMenu) {
    const block = createBlockMenu(editor, callbacks.onStateChange);
    state.blockMenu = block;
    bar.appendChild(block.root);
  }

  const scroll = document.createElement('div');
  scroll.className = 'g5-gurumi-toolbar-scroll';

  scroll.appendChild(divider());
  state.undo = toolBtn({ label: '실행 취소', icon: 'undo', onClick: () => editor.chain().focus().undo().run() });
  state.redo = toolBtn({ label: '다시 실행', icon: 'redo', onClick: () => editor.chain().focus().redo().run() });
  scroll.appendChild(state.undo);
  scroll.appendChild(state.redo);

  scroll.appendChild(divider());
  if (f.bold) {
    state.bold = toolBtn({ label: '굵게', icon: 'bold', onClick: () => editor.chain().focus().toggleBold().run() });
    scroll.appendChild(state.bold);
  }
  if (f.italic) {
    state.italic = toolBtn({ label: '기울임', icon: 'italic', onClick: () => editor.chain().focus().toggleItalic().run() });
    scroll.appendChild(state.italic);
  }

  // [글자색·형광펜] Bold/Italic 다음 — 작성/수정일: 2026-08-12
  if (f.color) {
    state.colorMenu = createColorMenu(editor, callbacks.onStateChange);
    scroll.appendChild(state.colorMenu.root);
  }

  if (f.link) {
    scroll.appendChild(divider());
    state.link = toolBtn({ label: '링크 삽입', icon: 'link', onClick: handlers.openLink });
    state.unlink = toolBtn({ label: '링크 제거', icon: 'unlink', onClick: () => editor.chain().focus().unsetLink().run() });
    scroll.appendChild(state.link);
    scroll.appendChild(state.unlink);
  }

  if (f.image || f.youtube) {
    scroll.appendChild(divider());
    if (f.image) {
      state.image = toolBtn({ label: '이미지 첨부', icon: 'image', onClick: handlers.openImage });
      scroll.appendChild(state.image);
    }
    if (f.youtube) {
      state.youtube = toolBtn({ label: 'YouTube 영상 삽입', icon: 'video', onClick: handlers.openYoutube });
      scroll.appendChild(state.youtube);
    }
  }

  if (f.align) {
    scroll.appendChild(divider());
    state.left = toolBtn({ label: '왼쪽 정렬', icon: 'alignLeft', onClick: () => handlers.setAlignment('left') });
    state.center = toolBtn({ label: '가운데 정렬', icon: 'alignCenter', onClick: () => handlers.setAlignment('center') });
    state.right = toolBtn({ label: '오른쪽 정렬', icon: 'alignRight', onClick: () => handlers.setAlignment('right') });
    scroll.appendChild(state.left);
    scroll.appendChild(state.center);
    scroll.appendChild(state.right);
  }

  if (f.image) {
    state.alt = document.createElement('button');
    state.alt.type = 'button';
    state.alt.className = 'g5-gurumi-tool-btn g5-gurumi-tool-btn-text';
    state.alt.textContent = '대체텍스트';
    state.alt.hidden = true;
    state.alt.addEventListener('click', (e) => {
      e.preventDefault();
      handlers.openAlt();
    });
    scroll.appendChild(state.alt);
  }

  bar.appendChild(scroll);

  function updateToolbarState(s) {
    if (state.undo) state.undo.disabled = !s.canUndo;
    if (state.redo) state.redo.disabled = !s.canRedo;
    if (state.bold) state.bold.classList.toggle('is-active', s.bold);
    if (state.italic) state.italic.classList.toggle('is-active', s.italic);
    if (state.link) state.link.classList.toggle('is-active', s.link);
    if (state.unlink) state.unlink.disabled = !s.link;
    if (state.left) state.left.classList.toggle('is-active', s.left);
    if (state.center) state.center.classList.toggle('is-active', s.center);
    if (state.right) state.right.classList.toggle('is-active', s.right);
    if (state.alt) {
      // [대체텍스트] 이미지 노드 선택 시에만 표시 — 작성/수정일: 2026-07-29
      state.alt.hidden = !s.image;
      state.alt.setAttribute('aria-hidden', s.image ? 'false' : 'true');
    }
    state.blockMenu?.refreshActive?.();
    // [글자색·형광펜] 선택 변경 시 버튼 표시 갱신 — 작성/수정일: 2026-08-12
    state.colorMenu?.refresh?.();
  }

  return {
    bar,
    updateToolbarState,
    setUploading: (uploading) => {
      if (state.image) {
        state.image.disabled = uploading;
        state.image.innerHTML = '';
        state.image.appendChild(iconSvg(uploading ? 'loader' : 'image'));
      }
    },
  };
}
